/**
 * Per-chat UI flags for background / cross-route state.
 * Transcripts stay in React (builder) + chat-history — signals only hold badges.
 *
 * Ownership:
 * - `streaming` / `awaitingInput` — applyChatFrame (multiplex deliver) is source of truth.
 *   setChatLive / markChatPending are optimistic only (send / hydrate / crash safety).
 * - `building` — focused builder sets via setChatBuilding (live build/test phase + busy).
 *   applyChatFrame clears it on HITL parks and terminal stream ends (background-safe).
 * - Soft `parked` stream-ends keep `building` so phase-continue gaps do not flap idle.
 *
 * Sidebar paint (one path): resolveChatRowStatus → needs_input > building > working > idle.
 */

import { frameText } from "@/shared/builder-session";
import { hitlFramesFromDurableChunk } from "@/features/chat/builder/sse/durable-chunk-hitl";
import { shouldIgnoreLateReviewPark } from "@/features/chat/builder/review-approval";

type SignalSubscriber = () => void;

type Signal<T> = {
  get(): T;
  set(value: T): void;
  update(fn: (current: T) => T): void;
  subscribe(listener: SignalSubscriber): () => void;
  (): T;
};

function signal<T>(initial: T): Signal<T> {
  let value = initial;
  const listeners = new Set<SignalSubscriber>();
  const api = (() => value) as Signal<T>;
  api.get = () => value;
  api.set = (next) => {
    if (Object.is(value, next)) return;
    value = next;
    for (const listener of [...listeners]) listener();
  };
  api.update = (fn) => api.set(fn(value));
  api.subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  return api;
}

export type ChatStatus = "idle" | "running" | "error";

/** Flags only — no message transcript. */
export type ChatState = {
  streaming: boolean;
  status: ChatStatus;
  /** Parked on a HITL card; resumes once the user answers. */
  awaitingInput: boolean;
  /** Agent is materializing / updating the workflow canvas. */
  building: boolean;
  error?: string;
};

export type ChatActivity = {
  /** Chats with an active SSE / agent run (not parked, not building). */
  activeRuns: number;
  /** Chats waiting on the user (question / approval / inputs). */
  awaitingInput: number;
  /** Chats currently writing the canvas. */
  building: number;
};

/** Exclusive sidebar / activity badge — one chat has exactly one of these. */
export type ChatRowStatus = "building" | "needs_input" | "working" | "idle";

/**
 * needs_input > building > working > idle
 * HITL always wins (amber) — including mid-build Composio / question parks.
 */
export function resolveChatRowStatus(
  state: Pick<ChatState, "streaming" | "awaitingInput" | "building">,
): ChatRowStatus {
  if (state.awaitingInput) return "needs_input";
  if (state.building) return "building";
  if (state.streaming) return "working";
  return "idle";
}

/** Same priority as resolveChatRowStatus, over aggregated counts. */
export function resolveChatActivityStatus(activity: ChatActivity): ChatRowStatus | null {
  if (activity.awaitingInput > 0) return "needs_input";
  if (activity.building > 0) return "building";
  if (activity.activeRuns > 0) return "working";
  return null;
}

export function chatActivityBannerLabel(activity: ChatActivity): string | null {
  if (activity.awaitingInput > 0) {
    return activity.awaitingInput === 1
      ? "1 chat needs your input"
      : `${activity.awaitingInput} chats need your input`;
  }
  if (activity.building > 0) {
    return activity.building === 1
      ? "Building a workflow…"
      : `Building ${activity.building} workflows…`;
  }
  if (activity.activeRuns > 0) {
    return activity.activeRuns === 1 ? "1 agent running" : `${activity.activeRuns} agents running`;
  }
  return null;
}

const chatStore = new Map<string, Signal<ChatState>>();

/** Which chat the UI is focused on. */
export const currentChatId = signal<string | null>(null);

/** Bumps on any chat-flag change so one subscribe covers the whole store. */
const chatSignalsEpoch = signal(0);

/** Aggregated busy counts — safe to read from any route without Context. */
export const chatActivity = signal<ChatActivity>({
  activeRuns: 0,
  awaitingInput: 0,
  building: 0,
});

function emptyState(): ChatState {
  return {
    streaming: false,
    status: "idle",
    awaitingInput: false,
    building: false,
  };
}

function recountActivity(): ChatActivity {
  let activeRuns = 0;
  let awaitingInput = 0;
  let building = 0;
  for (const entry of chatStore.values()) {
    const row = resolveChatRowStatus(entry.get());
    if (row === "building") building += 1;
    else if (row === "needs_input") awaitingInput += 1;
    else if (row === "working") activeRuns += 1;
  }
  return { activeRuns, awaitingInput, building };
}

function publish() {
  const next = recountActivity();
  const prev = chatActivity.get();
  if (
    prev.activeRuns !== next.activeRuns ||
    prev.awaitingInput !== next.awaitingInput ||
    prev.building !== next.building
  ) {
    chatActivity.set(next);
  }
  chatSignalsEpoch.update((n) => n + 1);
}

function patchChat(chatId: string, patch: (state: ChatState) => ChatState) {
  const chat = getChatSignal(chatId);
  const before = chat.get();
  const after = patch(before);
  if (Object.is(before, after)) return;
  if (
    before.streaming === after.streaming &&
    before.status === after.status &&
    before.awaitingInput === after.awaitingInput &&
    before.building === after.building &&
    before.error === after.error
  ) {
    return;
  }
  chat.set(after);
  publish();
}

/** One signal per chat — always initialize before updating. */
export function getChatSignal(chatId: string): Signal<ChatState> {
  const id = chatId.trim();
  let entry = chatStore.get(id);
  if (!entry) {
    entry = signal(emptyState());
    chatStore.set(id, entry);
  }
  return entry;
}

export function peekChatState(chatId: string): ChatState {
  return getChatSignal(chatId).get();
}

export function setCurrentChatId(chatId: string | null) {
  const next = chatId?.trim() || null;
  if (currentChatId.get() === next) return;
  currentChatId.set(next);
  chatSignalsEpoch.update((n) => n + 1);
}

/** Which chat the UI is showing — aliases for call sites. */
export function setFocusedSession(sessionId: string | null) {
  setCurrentChatId(sessionId);
}

export function getFocusedSession(): string | null {
  return currentChatId.get();
}

export function getChatActivity(): ChatActivity {
  return chatActivity.get();
}

/** Optimistic send — mark live before the server session id arrives. */
export function markChatPending(chatId: string) {
  const id = chatId.trim();
  if (!id) return;
  patchChat(id, (state) => ({
    ...state,
    streaming: true,
    status: "running",
    error: undefined,
  }));
}

/**
 * Route one multiplexed SSE frame into the correct chat flags.
 * Safe for background chats — initializes the signal if missing.
 */
export function applyChatFrame(chatId: string, frame: { event: string; data: unknown }) {
  const id = chatId.trim();
  if (!id || id.startsWith("pending:")) return;

  const { event, data } = frame;

  // Durable multiplex parks arrive as raw `chunk` — expand before badge updates.
  if (event === "chunk") {
    for (const expanded of hitlFramesFromDurableChunk(data)) {
      applyChatFrame(id, expanded);
    }
    return;
  }

  // stream-start / run-start = a new live run. Durable phase-continue often
  // emits run-start only. Do not clear park / force Working… while the
  // sidebar already shows awaiting input (soft HITL); resume retries would flap.
  if (event === "stream-start" || event === "run-start") {
    patchChat(id, (state) => {
      if (state.awaitingInput) {
        return {
          ...state,
          error: undefined,
        };
      }
      return {
        ...state,
        streaming: true,
        status: "running",
        awaitingInput: false,
        error: undefined,
      };
    });
    return;
  }

  if (event === "session") {
    patchChat(id, (state) => {
      if (state.awaitingInput) return state;
      return {
        ...state,
        streaming: true,
        status: "running",
        error: undefined,
      };
    });
    return;
  }

  // Review parks — Working ends when the card is on the wire, not at stream-end.
  // After Approve/Build, ignore late park frames until the next run attaches.
  // Bare `awaiting-input` is phase-only (no card) — do not arm sidebar yellow.
  if (
    event === "plan" ||
    event === "awaiting-approval" ||
    event === "awaiting-intent-approval" ||
    event === "user-question" ||
    event === "input-required" ||
    event === "build-paused" ||
    event === "composio-exec-approval"
  ) {
    if (shouldIgnoreLateReviewPark(id, event)) return;
    patchChat(id, (state) => ({
      ...state,
      streaming: false,
      building: false,
      status: "idle",
      awaitingInput: true,
    }));
    // Persist tools/text/cards before refresh — do not wait for a React effect.
    void import("@/features/chat/multi/history/chat-history").then((mod) => {
      mod.flushChatMessages(id);
    });
    return;
  }

  if (event === "text-delta") {
    patchChat(id, (state) => {
      // Parked review is sticky until the next stream-start / session.
      // Model abort noise must not reopen Working… after plan/HITL.
      if (state.awaitingInput) return state;
      return {
        ...state,
        streaming: true,
        status: "running",
      };
    });
    return;
  }

  if (
    event === "user-question-resolved" ||
    event === "input-required-resolved" ||
    event === "runtime-input-resolved" ||
    event === "composio-exec-resolved"
  ) {
    patchChat(id, (state) => ({ ...state, awaitingInput: false }));
    return;
  }

  if (event === "tool-call" || event === "tool-result" || event === "phase-change") {
    patchChat(id, (state) => {
      if (state.awaitingInput) return state;
      return {
        ...state,
        streaming: true,
        status: "running",
      };
    });
    return;
  }

  if (event === "error") {
    const message = frameText(data) || "Workflow run failed";
    patchChat(id, (state) => ({
      ...state,
      streaming: false,
      building: false,
      status: "error",
      error: message,
    }));
    return;
  }

  if (
    event === "stream-ended" ||
    event === "stream-end" ||
    event === "complete" ||
    event === "agent-stopped" ||
    event === "build-stalled"
  ) {
    const payload =
      data && typeof data === "object" ? (data as { status?: string; failed?: boolean }) : {};
    const endStatus = payload.status;
    const failed = payload.failed === true || endStatus === "error";
    const errored = failed || event === "agent-stopped" || event === "build-stalled";
    // Soft phase-continue parks keep Building…; terminal / stop / stall clear it
    // so background chats do not stick after the run dies.
    const softPark = !errored && endStatus === "parked";

    patchChat(id, (state) => ({
      ...state,
      streaming: false,
      building: softPark ? state.building : false,
      status: errored ? "error" : "idle",
      // Soft parks must not invent yellow here — card frames do. If the card was
      // only persisted on the session doc (common for background hubs), peek once.
      awaitingInput: state.awaitingInput,
    }));
    if (softPark) {
      void import("./arm-sidebar-awaiting").then((mod) => {
        mod.armSidebarAwaitingFromSession(id);
      });
    }
  }
}

export function isChatStreaming(chatId: string): boolean {
  const id = chatId.trim();
  if (!id || !chatStore.has(id)) return false;
  return chatStore.get(id)!.get().streaming;
}

export function isChatAwaitingInput(chatId: string): boolean {
  const id = chatId.trim();
  if (!id || !chatStore.has(id)) return false;
  return chatStore.get(id)!.get().awaitingInput;
}

export function setChatAwaitingInput(chatId: string, awaiting: boolean) {
  const id = chatId.trim();
  if (!id) return;
  patchChat(id, (state) => {
    if (state.awaitingInput === awaiting && !(awaiting && (state.streaming || state.building))) {
      return state;
    }
    return {
      ...state,
      awaitingInput: awaiting,
      // Park owns the UI — sticky Working… / Building… must not outlive the card.
      ...(awaiting
        ? {
            streaming: false,
            building: false,
            status: state.status === "error" ? "error" : "idle",
          }
        : {}),
    };
  });
}

export function setChatStreaming(chatId: string, streaming: boolean) {
  const id = chatId.trim();
  if (!id) return;
  patchChat(id, (state) => {
    // Park owns the badge — do not reopen Working… while awaiting HITL.
    if (streaming && state.awaitingInput) return state;
    if (state.streaming === streaming) return state;
    return {
      ...state,
      streaming,
      status: streaming ? "running" : state.status === "error" ? "error" : "idle",
    };
  });
}

export function isChatBuilding(chatId: string): boolean {
  const id = chatId.trim();
  if (!id || !chatStore.has(id)) return false;
  return chatStore.get(id)!.get().building;
}

export function setChatBuilding(chatId: string, building: boolean) {
  const id = chatId.trim();
  if (!id) return;
  patchChat(id, (state) => (state.building === building ? state : { ...state, building }));
}

/** One subscription for all per-chat flag changes + focus + activity. */
export function subscribeChatSignals(onChange: () => void): () => void {
  const unsubEpoch = chatSignalsEpoch.subscribe(onChange);
  const unsubFocus = currentChatId.subscribe(onChange);
  return () => {
    unsubEpoch();
    unsubFocus();
  };
}

/** Test helper */
export function resetChatSignalsForTests() {
  chatStore.clear();
  currentChatId.set(null);
  chatActivity.set({ activeRuns: 0, awaitingInput: 0, building: 0 });
  chatSignalsEpoch.set(0);
}
